import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Popover,
  Button,
  Chip,
  Tooltip,
  styled,
  tableCellClasses,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import { isMobile } from "../../globalStyles/mobile";
import Pagination from "../pagination";
import FilterModal from "../filterModal";
import TableHeader from "../table-header";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import ReturnViewModal from "../return-view-modal";
import { formatDate } from "../../utils/formatDate";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: "6px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
}));

/**
 * Status pill colours.
 *
 * Grouped by what the status MEANS to an operator, not one colour per value:
 * a return that has landed and settled is done (green); one still moving is in
 * flight (blue); one that needs a human is a problem (red); the rest are
 * neutral. Keyed on the display label control-tower stores, and covering BOTH
 * providers' vocabularies (Pipe17's twelve states and Shopify's five) because
 * one shared collection feeds this table.
 *
 * An unmapped status falls through to the neutral chip rather than crashing or
 * rendering an unstyled cell — providers add enum members without telling us.
 */
const STATUS_COLORS = {
  // settled / done
  Completed: { bg: "#ECFDF3", fg: "#027A48" },
  Refunded: { bg: "#ECFDF3", fg: "#027A48" },
  Accepted: { bg: "#ECFDF3", fg: "#027A48" },
  Received: { bg: "#ECFDF3", fg: "#027A48" },
  CLOSED: { bg: "#ECFDF3", fg: "#027A48" },
  // in flight
  Authorized: { bg: "#EFF8FF", fg: "#175CD3" },
  Shipped: { bg: "#EFF8FF", fg: "#175CD3" },
  Expected: { bg: "#EFF8FF", fg: "#175CD3" },
  "Partially Received": { bg: "#EFF8FF", fg: "#175CD3" },
  OPEN: { bg: "#EFF8FF", fg: "#175CD3" },
  REQUESTED: { bg: "#EFF8FF", fg: "#175CD3" },
  // needs attention
  Failed: { bg: "#FEF3F2", fg: "#B42318" },
  Expired: { bg: "#FEF3F2", fg: "#B42318" },
  Cancelled: { bg: "#FEF3F2", fg: "#B42318" },
  CANCELED: { bg: "#FEF3F2", fg: "#B42318" },
  DECLINED: { bg: "#FEF3F2", fg: "#B42318" },
};
const NEUTRAL_STATUS = { bg: "#F2F4F7", fg: "#344054" };

const cellText = (value) => (
  <Typography
    fontSize="12px"
    color="#667085"
    fontWeight={400}
    fontFamily={"Poppins"}
  >
    {value === 0 ? 0 : value || "--"}
  </Typography>
);

/** Money is stored as a String exactly as the provider reported it (see
 *  return.model.js). Format for display only — never re-derive the number, or
 *  the portal starts disagreeing with the provider's own refund figure. */
const money = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return "--";
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  return `${currency ? `${currency} ` : ""}${n.toFixed(2)}`;
};

const Returns = ({
  data,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  onChangeFilter,
  filterData,
  onSync,
}) => {
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [anchorElPopover, setAnchorElPopover] = useState(null);
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
  });

  const startIndex = page * (filterData?.limit || 15);

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData.columns.find((x) => x.key === filterKey));
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handleSaveFilter = (selectedOptions, filterKey, sortOrder, searchKey) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey: searchKey,
    });
    handleCloseFilter();
  };

  const handlePopoverOpen = (event, item) => {
    setAnchorElPopover(event.currentTarget);
    setSelectedReturn(item);
  };

  const handlePopoverClose = () => setAnchorElPopover(null);

  const handleViewClick = (item) => {
    setSelectedReturn(item);
    setOpenViewModal(true);
    setAnchorElPopover(null);
  };

  const NoDataImage = () => (
    <div style={{ width: "100%", justifyContent: "center", display: "flex" }}>
      <img
        src="no_data_rateCard.png"
        alt="No data available"
        style={{ objectFit: "fill", minHeight: "100%" }}
      />
    </div>
  );

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      width: "4%",
      render: (item, index) => cellText(startIndex + index + 1),
    },
    {
      key: "name",
      title: "RETURN",
      filterKey: "name",
      handleOpenFilter,
      render: (x) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            fontSize="12px"
            color="#344054"
            fontWeight={500}
            fontFamily={"Poppins"}
          >
            {x?.name || x?.external_id || "--"}
          </Typography>
          {/* Pipe17's own "why is this return stuck" signal. Shown inline
              because a stuck return looks identical to a healthy one in every
              other column, which is exactly how it goes unnoticed. */}
          {x?.exceptions?.length ? (
            <Tooltip title={x.exceptions.join(", ")}>
              <WarningAmberIcon sx={{ fontSize: 14, color: "#B54708" }} />
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
    {
      key: "status",
      title: "STATUS",
      filterKey: "status",
      handleOpenFilter,
      render: (x) => {
        const color = STATUS_COLORS[x?.status] || NEUTRAL_STATUS;
        return (
          <Tooltip title={x?.provider_status ? `Provider: ${x.provider_status}` : ""}>
            <Chip
              label={x?.status || "--"}
              size="small"
              sx={{
                backgroundColor: color.bg,
                color: color.fg,
                fontFamily: "Poppins",
                fontSize: "11px",
                fontWeight: 500,
                height: "22px",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      key: "order_name",
      title: "ORDER",
      filterKey: "order_name",
      handleOpenFilter,
      render: (x) => cellText(x?.order_name),
    },
    {
      key: "total_quantity",
      title: "QTY",
      width: "5%",
      render: (x) => cellText(x?.total_quantity),
    },
    {
      key: "received",
      title: "RECEIVED",
      width: "7%",
      // Summed across the line items rather than stored: Pipe17 reports receipt
      // per line, and a return is commonly received in parts, so there is no
      // single top-level "received quantity" to read.
      render: (x) =>
        cellText(
          (x?.line_items || []).reduce(
            (sum, l) => sum + (Number(l?.quantity_received) || 0),
            0
          )
        ),
    },
    {
      key: "total",
      title: "REFUND",
      render: (x) => cellText(money(x?.total, x?.currency)),
    },
    {
      key: "tracking",
      title: "TRACKING",
      render: (x) =>
        cellText(x?.tracking_number || x?.return_shipments?.[0]?.tracking_number),
    },
    {
      key: "oms_provider",
      title: "SOURCE",
      filterKey: "oms_provider",
      handleOpenFilter,
      render: (x) => cellText(x?.oms_provider),
    },
    {
      // The sales channel, read straight off the stored `channel` field —
      // SOURCE is the system the return was fetched FROM (Pipe17), not where
      // the sale happened. It is resolved from the return's order at
      // ingestion, and filled in lazily for older returns as they are paged
      // over (see control-tower's return-channel.js), which is what makes it
      // filterable like any other column.
      key: "channel",
      title: "CHANNEL",
      filterKey: "channel",
      handleOpenFilter,
      render: (x) => (
        <Tooltip
          title={
            x?.channel
              ? ""
              : `No order matching ${x?.order_name || "this return"} was found for this client, so its channel is not known.`
          }
        >
          <span>{cellText(x?.channel)}</span>
        </Tooltip>
      ),
    },
    {
      key: "requested_at",
      title: "REQUESTED",
      filterKey: "requested_at",
      handleOpenFilter,
      render: (x) => cellText(x?.requested_at ? formatDate(x.requested_at, "") : null),
    },
    {
      key: "action",
      title: "",
      width: "3%",
      render: (x) => (
        <Box sx={{ display: "flex", boxShadow: "none" }}>
          <IconButton
            aria-label="more actions"
            onClick={(e) => handlePopoverOpen(e, x)}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const MobileCardView = ({ data }) => (
    <Box>
      {data.map((x, index) => (
        <Card
          key={index}
          sx={{ marginBottom: 2, backgroundColor: "#ffff", borderRadius: 4 }}
        >
          <CardContent>
            <Grid container spacing={1}>
              {[
                ["Return", x?.name || x?.external_id],
                ["Status", x?.status],
                ["Order", x?.order_name],
                ["Qty", x?.total_quantity],
                ["Refund", money(x?.total, x?.currency)],
                ["Source", x?.oms_provider],
                ["Channel", x?.channel],
                ["Requested", x?.requested_at ? formatDate(x.requested_at, "") : null],
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <Grid item xs={6}>
                    <Typography
                      fontSize={14}
                      fontFamily="poppins"
                      color="#333333"
                      fontWeight={600}
                      textAlign="left"
                    >
                      {label}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      fontSize={14}
                      fontFamily="Poppins"
                      color="#606060"
                      fontWeight={500}
                      noWrap
                      overflow="hidden"
                      textOverflow="ellipsis"
                      textAlign="left"
                    >
                      : {value === 0 ? 0 : value || "--"}
                    </Typography>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
            <Box mt={2} justifyContent={"flex-end"} display="flex">
              <Button size="small" onClick={() => handleViewClick(x)}>
                View
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <div style={{ minWidth: "100%" }}>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        pb={1}
      >
        <Tooltip title="Import a client's Pipe17 return history">
          <Button
            variant="outlined"
            size="small"
            startIcon={<SyncIcon />}
            onClick={onSync}
            sx={{
              textTransform: "none",
              fontFamily: "Poppins",
              fontSize: "12px",
            }}
          >
            Sync Returns
          </Button>
        </Tooltip>
      </Box>

      <div
        style={{
          overflowY: "scroll",
          display: "flex",
          maxHeight: isMobile() ? "60vh" : `calc(100vh - 250px)`,
          width: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {isMobile() ? (
          <MobileCardView data={data} />
        ) : (
          <TableContainer
            sx={{ overflowY: "auto", paddingInline: 0.4, maxHeight: "100%" }}
          >
            <Table
              stickyHeader
              aria-label="returns table"
              sx={{ border: "1px solid var(--border-color)" }}
            >
              <TableHead>
                <StyledTableRow>
                  {TableSchema.map((column) => (
                    <TableHeader
                      key={column.key}
                      column={column}
                      filterData={filterData}
                      filterKey={column.filterKey}
                      handleOpenFilter={column.handleOpenFilter}
                      title={column.title}
                    />
                  ))}
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 ? (
                  <StyledTableRow>
                    <TableCell colSpan={TableSchema.length} align="center">
                      <NoDataImage />
                    </TableCell>
                  </StyledTableRow>
                ) : (
                  data?.map((x, rowIndex) => (
                    <StyledTableRow key={x?._id || rowIndex}>
                      {TableSchema.map((column, colIndex) => (
                        <StyledTableCell
                          key={column.key || colIndex}
                          sx={{ width: column.width }}
                        >
                          {column.render ? column.render(x, rowIndex) : "--"}
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      <Pagination
        dataCount={dataCount}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        disableNext={dataCount <= (page + 1) * (filterData?.limit || 15)}
      />

      <Popover
        open={Boolean(anchorElPopover)}
        anchorEl={anchorElPopover}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: isMobile() ? "bottom" : "top",
          horizontal: isMobile() ? "center" : "left",
        }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "none",
            border: "1px solid #EAECF0",
            borderRadius: "4px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "10px",
            boxShadow: "none",
            gap: 2,
          }}
        >
          <Button
            style={{
              color: "#333333",
              justifyContent: "flex-start",
              textTransform: "none",
            }}
            onClick={() => handleViewClick(selectedReturn)}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <ViewIcon />
              <Typography
                fontFamily={"poppins"}
                fontSize={"16px"}
                color={"#333333"}
                fontWeight={500}
              >
                View
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>

      <ReturnViewModal
        data={selectedReturn}
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
      />

      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedfilterData?.options}
          selectedValues={selectedfilterData?.filter}
          onSave={handleSaveFilter}
          currentSortOrder={selectedfilterData?.sort}
          filterKey={selectedfilterData?.key}
          hasMore={selectedfilterData?.loadMoreButton}
          displayKey={selectedfilterData?.displayKey}
          id={selectedfilterData?.id}
          fetchApi={selectedfilterData?.fetchApi}
          searchKey={selectedfilterData?.searchKey}
          type={selectedfilterData?.type}
          placeholderKey={selectedfilterData?.placeholderKey}
        />
      </Popover>
    </div>
  );
};

export default Returns;
